import {
  ChakraBox,
  DocumentInfoCard,
  FAIconName,
  ModalV2,
  ChakraFlex,
  TextInputV2,
  FireflyButton,
  ChakraVStack,
} from '@equip.health/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import type { AttachmentType } from '~/lib/constants/AddPatientNoteModal.constants';
import { useEditPatientNoteFormContext } from '~/lib/contexts/EditPatientNoteFormContext';
import { NoteEvents } from '~/analytics';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { NOTE_ATTACHMENT_SOURCES } from '~/lib/constants/notes';

interface AttachDocumentsModalProps {
  attachedDocuments?: AttachmentType[];
  isOpen: boolean;
  onClose: () => void;
  documents: PatientDocumentV2[];
  id: string;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetching?: boolean;
  source?: string;
  setSearchTerm?: (term: string) => void;
  onUploadSuccess?: (docs: AttachmentType[]) => void;
}

const DEBOUNCE_DELAY = 500;

const NOTE_FILE_SOURCE = 'NOTE';
const INFECTED_FILE_STATUS = 'INFECTED';
const INVALID_FILE_STATUS = 'INVALID';

const DISABLE_REASON_MESSAGES: Record<string, string> = {
  FILE_FAILED_SCAN:
    'This file failed a security scan and cannot be linked to notes.',
  FILE_PROCESSING:
    'This file is still being processed and cannot be linked to notes yet.',
  NOTE_SOURCE:
    'This file was uploaded before note linking was supported and cannot be linked to other notes.',
};

const AttachDocumentsModal = ({
  attachedDocuments = [],
  documents,
  fetchNextPage,
  hasNextPage,
  isFetching,
  setSearchTerm,
  isOpen,
  source,
  onClose,
  id,
  onUploadSuccess,
}: AttachDocumentsModalProps) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedDocumentInfo, setSelectedDocumentInfo] =
    useState<AttachmentType[]>(attachedDocuments);
  const { trackEvent } = useAnalytics();
  const { setUploadedFiles } = useEditPatientNoteFormContext();

  const onSearch = (v: string) => {
    setSearchTerm?.(v);
  };
  const onLoadMoreDocuments = useCallback(async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  const debouncedSearch = useRef(
    debounce((v: string) => onSearch(v.trim()), DEBOUNCE_DELAY),
  ).current;
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleSearch = (v: string) => {
    setInputValue(v);
    debouncedSearch(v);
  };

  const isDocumentSelected = (documentId: string) => {
    return selectedDocumentInfo.some(
      (doc) => doc.attachmentExternalId === documentId,
    );
  };

  const trackNoteFormEvent = (
    event: NoteEvents,
    source: string,
    additionalProps?: Record<string, string>,
  ) => {
    trackEvent(event, {
      Source: source,
      ...additionalProps,
    });
  };
  const handleDocumentSelect = (
    documentExternalId: string,
    fileName: string,
    isSelected: boolean,
  ) => {
    setSelectedDocumentInfo((prev) => {
      if (!isSelected) {
        return prev.filter(
          (doc) => doc.attachmentExternalId !== documentExternalId,
        );
      } else {
        return [
          ...prev,
          {
            attachmentExternalId: documentExternalId,
            fileName,
            statusCode: null,
          },
        ];
      }
    });
  };
  const handleAttach = () => {
    if (source === NOTE_ATTACHMENT_SOURCES.INQUIRY_PANEL) {
      trackNoteFormEvent(
        NoteEvents.attachButtonClickedFromSelectDocumentModal,
        AnalyticsSourceValues.INQUIRY_DETAILS,
      );
    } else {
      trackNoteFormEvent(
        NoteEvents.attachButtonClickedFromSelectDocumentModal,
        AnalyticsSourceValues.NOTES_TAB,
      );
    }
    setUploadedFiles(() => [...selectedDocumentInfo]);
    onUploadSuccess?.([...selectedDocumentInfo]);
    onClose();
  };
  const getDocumentDisableReason = (
    document: PatientDocumentV2,
  ): string | undefined => {
    if (document.fileSource === NOTE_FILE_SOURCE) {
      return DISABLE_REASON_MESSAGES.NOTE_SOURCE;
    }
    if (document.uploadedViaFileService && document.fileScanStatus == null) {
      return DISABLE_REASON_MESSAGES.FILE_PROCESSING;
    }
    if (
      document.fileScanStatus === INVALID_FILE_STATUS ||
      document.fileScanStatus === INFECTED_FILE_STATUS
    ) {
      return DISABLE_REASON_MESSAGES.FILE_FAILED_SCAN;
    }
    return undefined;
  };

  return (
    <ModalV2
      id={`${id}__attach-documents-modal`}
      isOpen={isOpen}
      onClose={() => {
        if (source === NOTE_ATTACHMENT_SOURCES.INQUIRY_PANEL) {
          trackNoteFormEvent(
            NoteEvents.selectDocumentModalClosed,
            AnalyticsSourceValues.INQUIRY_DETAILS,
          );
        } else {
          trackNoteFormEvent(
            NoteEvents.selectDocumentModalClosed,
            AnalyticsSourceValues.NOTES_TAB,
          );
        }
        onClose();
      }}
      size="xl"
      title="Select documents to attach"
    >
      <ChakraVStack alignItems="stretch" gap="24px">
        <TextInputV2
          id={`${id}__search-input`}
          leftIcon={FAIconName.searchRegular}
          onChange={handleSearch}
          placeholder="Search documents"
          value={inputValue}
          onFocus={() => {
            if (source === NOTE_ATTACHMENT_SOURCES.INQUIRY_PANEL) {
              trackNoteFormEvent(
                NoteEvents.focusOnSearchInputFieldFromSelectDocumentModal,
                AnalyticsSourceValues.INQUIRY_DETAILS,
              );
            } else {
              trackNoteFormEvent(
                NoteEvents.focusOnSearchInputFieldFromSelectDocumentModal,
                AnalyticsSourceValues.NOTES_TAB,
              );
            }
          }}
        />

        <ChakraFlex
          data-testid="documents-scroll-container"
          flexDir="column"
          flexShrink={0}
          gap="8px"
          height="calc(100vh - 24rem)"
          onScroll={(e) => {
            const target = e.target as HTMLDivElement;
            const scrolledToBottom =
              target.scrollHeight - target.scrollTop - target.clientHeight < 1;
            if (scrolledToBottom && hasNextPage && !isFetching) {
              onLoadMoreDocuments();
            }
          }}
          overflowY="auto"
        >
          {documents.map((document) => {
            const reason = getDocumentDisableReason(document);
            const isDisabled = !!reason;
            return (
              <ChakraBox flexShrink={0} key={document.documentExternalId}>
                <DocumentInfoCard
                  disabledReason={reason}
                  documentDetails={{
                    createdAt: document?.createdAt,
                    documentExternalId: document?.documentExternalId,
                    downloadUrl: document?.downloadUrl,
                    fileName: document?.fileName,
                    fileScanStatus: document?.fileScanStatus,
                    thumbnailUrl: document?.thumbnailUrl,
                  }}
                  isDisabled={isDisabled}
                  isDocumentSelected={isDocumentSelected(
                    document.documentExternalId,
                  )}
                  onClick={({ documentExternalId, fileName, isSelected }) => {
                    if (isSelected) {
                      if (source === NOTE_ATTACHMENT_SOURCES.INQUIRY_PANEL) {
                        trackNoteFormEvent(
                          NoteEvents.documentSelectedFromSelectDocumentModal,
                          AnalyticsSourceValues.INQUIRY_DETAILS,
                          {
                            documentId: documentExternalId,
                          },
                        );
                      } else {
                        trackNoteFormEvent(
                          NoteEvents.documentSelectedFromSelectDocumentModal,
                          AnalyticsSourceValues.NOTES_TAB,
                          {
                            documentId: documentExternalId,
                          },
                        );
                      }
                    }
                    handleDocumentSelect(
                      documentExternalId,
                      fileName,
                      isSelected,
                    );
                  }}
                />
              </ChakraBox>
            );
          })}

          {isFetching && <div>Loading more...</div>}
        </ChakraFlex>
        <ChakraFlex justifyContent="space-between ">
          <FireflyButton
            id="cancel-attach-documents"
            leftIcon={FAIconName.timesRegular}
            onClick={() => {
              if (source === NOTE_ATTACHMENT_SOURCES.INQUIRY_PANEL) {
                trackNoteFormEvent(
                  NoteEvents.cancelButtonClickedFromSelectDocumentModal,
                  AnalyticsSourceValues.INQUIRY_DETAILS,
                );
              } else {
                trackNoteFormEvent(
                  NoteEvents.cancelButtonClickedFromSelectDocumentModal,
                  AnalyticsSourceValues.NOTES_TAB,
                );
              }
              onClose();
            }}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id="attach-documents"
            leftIcon={FAIconName.checkRegular}
            onClick={handleAttach}
            variant="primary"
          >
            Attach{' '}
            {selectedDocumentInfo.length > 0 ? selectedDocumentInfo.length : ''}{' '}
            {selectedDocumentInfo.length === 1 ? 'document' : 'documents'}
          </FireflyButton>
        </ChakraFlex>
      </ChakraVStack>
    </ModalV2>
  );
};

export default AttachDocumentsModal;
